import { useScroll } from "ahooks";
import { useMemo } from "react";

import { BaseLink } from "~/components/common/base-link";
import { IdleQueue } from "~/components/common/idle-queue";
import { DynamicHeaderAvatar, DynamicHeaderSubscribeButton } from "~/components/header/dynamics";
import { HeaderContentBookmark } from "~/components/header/header-content-bookmark";
import { HeaderContentCommentTrigger } from "~/components/header/header-content-comment-trigger";
import { HeaderContentShareWidgetDrawer } from "~/components/header/header-content-share-widget-drawer";
import { HeaderMyNews } from "~/components/header/header-my-news";
import { ActionButton, AvatarContainer, StyledIconSearch } from "~/components/header/styles";
import type { ActionButtonProps, LogoProps } from "~/components/header/styles";
import type {
  Feature,
  HeaderConfiguration,
  ResponsiveFeature,
  ResponsiveLogoVariant,
} from "~/components/header/types";
import { useCurrentPageType } from "~/lib/router/hooks";
import type { OptionalBreakpointKey, RequiredBreakpointKey } from "~/lib/styles/responsive";
import { page as SearchPage } from "~/pages/search";

export const useSwapOnScrollEffect = (threshold: number) => {
  const { top } = useScroll() ?? { top: 0 };
  return { shouldSwap: top >= threshold };
};

export const useHeader = (shouldSwap: boolean, headerConfiguration?: HeaderConfiguration) => {
  const featureConfigurations = useMemo(() => {
    // If not provided, use default responsive features
    const responsiveFeatures: ResponsiveFeature = {
      desktopUp: {
        default: ["subscription", "search", "dashboard"],
      },
      largeDesktopUp: {
        default: ["subscription", "search", "my-news", "dashboard"],
      },
      mediumDesktopUp: {
        default: ["subscription", "search", "my-news", "dashboard"],
      },
      mobileUp: {
        default: ["dashboard"],
      },
      tabletUp: {
        default: ["subscription", "my-news", "dashboard"],
      },
      ...headerConfiguration?.responsiveFeatures,
    };

    const computeFeatureConfiguration = (feature: Feature) => {
      const computeFeatureListForBreakpoint = (
        breakpoint: RequiredBreakpointKey | OptionalBreakpointKey,
      ) => {
        const featureForBreakpoint = responsiveFeatures[breakpoint];
        if (!featureForBreakpoint) return [];
        const features = shouldSwap
          ? featureForBreakpoint.swapped ?? featureForBreakpoint.default
          : featureForBreakpoint.default;
        return features;
      };

      const responsiveVariants: ActionButtonProps["$responsiveVariants"] = {
        desktopUp: computeFeatureListForBreakpoint("desktopUp").includes(feature)
          ? "show"
          : "hidden",
        largeDesktopUp: computeFeatureListForBreakpoint("largeDesktopUp").includes(feature)
          ? "show"
          : "hidden",
        mediumDesktopUp: computeFeatureListForBreakpoint("mediumDesktopUp").includes(feature)
          ? "show"
          : "hidden",
        mobileUp: computeFeatureListForBreakpoint("mobileUp").includes(feature) ? "show" : "hidden",
        tabletUp: computeFeatureListForBreakpoint("tabletUp").includes(feature) ? "show" : "hidden",
      };

      const isEnabled = Object.values(responsiveVariants).includes("show");

      return {
        isEnabled,
        responsiveVariants,
      };
    };

    const subscription = computeFeatureConfiguration("subscription");
    const dashboard = computeFeatureConfiguration("dashboard");
    const search = computeFeatureConfiguration("search");
    const myNews = computeFeatureConfiguration("my-news");
    const contentShare = computeFeatureConfiguration("content-share");
    const contentBookmark = computeFeatureConfiguration("content-bookmark");
    const contentComment = computeFeatureConfiguration("content-comment");
    return {
      contentBookmark,
      contentComment,
      contentShare,
      dashboard,
      myNews,
      search,
      subscription,
    };
  }, [headerConfiguration, shouldSwap]);

  const currentPageType = useCurrentPageType();
  const mastheadQueryParameter = {
    module: "masthead",
    pgtype: currentPageType,
  };

  const renderFeatures = (): undefined | JSX.Element => {
    if (!featureConfigurations) return;
    const {
      contentBookmark,
      contentComment,
      contentShare,
      dashboard,
      myNews,
      search,
      subscription,
    } = featureConfigurations;
    const { pathname: searchPathname, query: searchQuery } = SearchPage.route({});
    return (
      <>
        <IdleQueue>
          {subscription.isEnabled && (
            <ActionButton $responsiveVariants={subscription.responsiveVariants}>
              <DynamicHeaderSubscribeButton
                variant={headerConfiguration?.appBarVariant === "style" ? "dark" : undefined}
              />
            </ActionButton>
          )}
        </IdleQueue>

        {search.isEnabled && (
          <ActionButton $responsiveVariants={search.responsiveVariants}>
            <BaseLink
              anchorProps={{ "aria-label": "search" }}
              as="/search"
              pathname={searchPathname}
              query={{ ...searchQuery, ...mastheadQueryParameter }}
            >
              <StyledIconSearch />
            </BaseLink>
          </ActionButton>
        )}

        {myNews.isEnabled && (
          <ActionButton $responsiveVariants={myNews.responsiveVariants}>
            <BaseLink
              anchorProps={{ "aria-label": "my news" }}
              as="/mynews"
              pathname="mynews"
              query={mastheadQueryParameter}
            >
              <HeaderMyNews />
            </BaseLink>
          </ActionButton>
        )}

        {contentComment.isEnabled && (
          <ActionButton $responsiveVariants={contentComment.responsiveVariants}>
            <HeaderContentCommentTrigger />
          </ActionButton>
        )}
        {contentShare.isEnabled && (
          <ActionButton $responsiveVariants={contentShare.responsiveVariants}>
            <HeaderContentShareWidgetDrawer />
          </ActionButton>
        )}
        {contentBookmark.isEnabled && (
          <ActionButton $responsiveVariants={contentBookmark.responsiveVariants}>
            <HeaderContentBookmark />
          </ActionButton>
        )}
        {dashboard.isEnabled && (
          <ActionButton $responsiveVariants={dashboard.responsiveVariants}>
            <AvatarContainer>
              <IdleQueue>
                <DynamicHeaderAvatar />
              </IdleQueue>
            </AvatarContainer>
          </ActionButton>
        )}
      </>
    );
  };

  const responsiveLogoVariant = useMemo(() => {
    // If not provided, use default responsive logo variant
    const responsiveLogoVariant: ResponsiveLogoVariant = {
      desktopUp: {
        default: "logo",
      },
      largeDesktopUp: {
        default: "logo",
      },
      mobileUp: {
        default: "full",
      },
      tabletUp: {
        default: "full",
      },
      xLargeDesktopUp: {
        default: "full",
      },
      ...headerConfiguration?.responsiveLogoVariant,
    };

    const computeVariantForBreakpoint = (
      breakpoint: RequiredBreakpointKey | OptionalBreakpointKey,
    ) => {
      const variantForBreakpoint = responsiveLogoVariant[breakpoint];
      if (!variantForBreakpoint) return "full";
      const features = shouldSwap
        ? variantForBreakpoint.swapped ?? variantForBreakpoint.default
        : variantForBreakpoint.default;
      return features;
    };

    const responsiveVariants: LogoProps["$responsiveVariants"] = {
      desktopUp: computeVariantForBreakpoint("desktopUp"),
      largeDesktopUp: computeVariantForBreakpoint("largeDesktopUp"),
      mobileUp: computeVariantForBreakpoint("mobileUp"),
      tabletUp: computeVariantForBreakpoint("tabletUp"),
      xLargeDesktopUp: computeVariantForBreakpoint("xLargeDesktopUp"),
    };

    return responsiveVariants;
  }, [headerConfiguration, shouldSwap]);

  return {
    featureConfigurations,
    mastheadQueryParameter,
    renderFeatures,
    responsiveLogoVariant,
  };
};
